<template>
  <div class="delivery-view">
    <h1 class="page-title">Lieferungen</h1>

    <div class="delivery-toolbar">
      <label class="missing-inv-filter">
        <input type="checkbox" v-model="onlyMissingInvoice" @change="onFilterChange" />
        Nur ohne Rechnungsnr.
      </label>
      <button class="btn-csv" @click="downloadPostausgabebuch">
        <i class="fas fa-file-csv"></i> Postausgabebuch CSV
      </button>
    </div>

    <data-table
        title="Lieferungen"
        :columns="columns"
        :data="deliveries"
        :loading="loading"
        :current-page="pagination.currentPage"
        :total-pages="pagination.totalPages"
        :total-count="pagination.totalItems"
        @search="handleSearch"
        @sort="handleSort"
        @page-change="onPageChanged"
        @per-page-change="onPerPageChanged"
    >
      <!-- Auftrag -->
      <template v-slot:order_number="{ item }">
        <router-link v-if="item.order_id" :to="`/auftraege/${item.order_id}`" class="text-decoration-none">
          {{ item.order_number }}
        </router-link>
        <span v-else class="text-muted">-</span>
      </template>

      <!-- Kunde -->
      <template v-slot:customer_name="{ item }">
        <router-link v-if="item.customer_id" :to="`/kunden/${item.customer_id}`" class="text-decoration-none">
          {{ item.customer_name }}
        </router-link>
        <span v-else>{{ item.customer_name }}</span>
      </template>

      <!-- Versandart -->
      <template v-slot:delivery_method="{ item }">
        {{ item.delivery_method_text || getDeliveryMethodLabel(item.delivery_method) }}
      </template>

      <!-- Rechnungsnr. — inline edit (#25) -->
      <template v-slot:invoice_number="{ item }">
        <span v-if="editingInvoiceId !== item.id" class="inv-cell" @click="startInvoiceEdit(item)">
          <span v-if="item.invoice_number">{{ item.invoice_number }}</span>
          <span v-else class="inv-missing">+ Re.-Nr.</span>
        </span>
        <span v-else class="inv-edit" @click.stop>
          <input
            v-model="invoiceDraft"
            maxlength="8"
            class="inv-input"
            @keyup.enter="saveInvoice(item)"
            @keyup.esc="cancelInvoiceEdit"
          />
          <button class="inv-save" @click="saveInvoice(item)"><i class="fas fa-check"></i></button>
          <button class="inv-cancel" @click="cancelInvoiceEdit"><i class="fas fa-times"></i></button>
        </span>
      </template>

      <!-- Aktionen -->
      <template v-slot:actions="{ item }">
        <div class="btn-group btn-group-sm">
          <button class="btn btn-outline-primary" @click="viewDelivery(item.id)" title="Anzeigen">
            <i class="fas fa-eye"></i>
          </button>
          <button class="btn btn-outline-secondary" @click="editDelivery(item.id)" title="Bearbeiten">
            <i class="fas fa-edit"></i>
          </button>
          <button class="btn btn-outline-danger" @click="deleteDelivery(item.id)" title="Löschen">
            <i class="fas fa-trash"></i>
          </button>
        </div>
      </template>
    </data-table>
  </div>
</template>

<script>
import DataTable from '@/components/DataTable.vue'
import api from '@/api'
import { OrderFormatter } from '@/services/orderService.js'

export default {
  name: 'DeliveryView',
  components: {
    DataTable
  },
  data() {
    return {
      deliveries: [],
      loading: false,
      searchQuery: '',
      columns: [
        { key: 'order_number', label: 'AU-Nr.', sortable: true },
        { key: 'customer_name', label: 'Kunde', sortable: true },
        { key: 'title', label: 'Objekt', sortable: true },
        { key: 'ordered_quantity', label: 'best. Menge', sortable: true },
        { key: 'delivered_quantity', label: 'gel. Menge', sortable: true },
        { key: 'packaging', label: 'Verpackung', sortable: true },
        { key: 'delivery_method', label: 'Versandart', sortable: true },
        { key: 'tracking_nr', label: 'Tracking-Nr.', sortable: false },
        { key: 'versand_kuerzel', label: 'Kürzel', sortable: false },
        { key: 'delivered_on', label: 'Datum', sortable: true },
        { key: 'invoice_number', label: 'Re.-Nr.', sortable: false },
        { key: 'actions', label: '', sortable: false }
      ],
      onlyMissingInvoice: false,
      editingInvoiceId: null,
      invoiceDraft: '',
      pagination: {
        currentPage: 1,
        totalPages: 0,
        totalItems: 0,
        perPage: 25
      },
      sort: {
        field: 'delivered_on',
        direction: 'desc'
      }
    }
  },

  created() {
    this.fetchDeliveries()
  },

  methods: {
    getDeliveryMethodLabel(method) {
      return OrderFormatter.getDeliveryMethodLabel(method)
    },

    // Postausgabebuch als CSV herunterladen (#18/#30).
    async downloadPostausgabebuch() {
      try {
        const blob = await api.getBlob('delivery/postausgabebuch')
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `postausgabebuch_${new Date().toISOString().slice(0, 10)}.csv`
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        console.error('CSV-Export fehlgeschlagen:', e)
        this.$toast?.error('Postausgabebuch-CSV konnte nicht geladen werden')
      }
    },

    async fetchDeliveries() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.currentPage,
          per_page: this.pagination.perPage,
          sort_by: this.sort.field,
          sort_direction: this.sort.direction,
          q: this.searchQuery
        }
        if (this.onlyMissingInvoice) params.missing_invoice = 1

        const response = await api.get('delivery', params)

        this.deliveries = response.data
        this.pagination.totalItems = response.meta.total_count
        this.pagination.totalPages = response.meta.total_pages
      } catch (error) {
        console.error('Error fetching deliveries:', error)
        this.$toast?.error('Fehler beim Laden der Lieferungen')
      } finally {
        this.loading = false
      }
    },

    handleSearch(query) {
      this.searchQuery = query
      this.pagination.currentPage = 1
      this.fetchDeliveries()
    },

    handleSort({ field, direction }) {
      this.sort.field = field
      this.sort.direction = direction
      this.fetchDeliveries()
    },

    onPageChanged(page) {
      this.pagination.currentPage = page
      this.fetchDeliveries()
    },

    onPerPageChanged(perPage) {
      this.pagination.perPage = perPage
      this.pagination.currentPage = 1
      this.fetchDeliveries()
    },

    onFilterChange() {
      this.pagination.currentPage = 1
      this.fetchDeliveries()
    },

    startInvoiceEdit(item) {
      this.editingInvoiceId = item.id
      this.invoiceDraft = item.invoice_number || ''
    },

    cancelInvoiceEdit() {
      this.editingInvoiceId = null
      this.invoiceDraft = ''
    },

    // Re.-Nr. lives on the Order, not the Delivery row (#25).
    async saveInvoice(item) {
      if (!item.order_id) { this.cancelInvoiceEdit(); return }
      try {
        await api.put(`orders/${item.order_id}`, { order: { invoice_number: this.invoiceDraft.trim() } })
        item.invoice_number = this.invoiceDraft.trim()
        this.$toast?.success('Rechnungsnr. gespeichert')
        if (this.onlyMissingInvoice) this.fetchDeliveries()
      } catch (e) {
        this.$toast?.error(e.message || 'Speichern fehlgeschlagen')
      } finally {
        this.cancelInvoiceEdit()
      }
    },

    viewDelivery(id) {
      this.$router.push(`/lieferungen/${id}`)
    },

    editDelivery(id) {
      this.$router.push({ path: `/lieferungen/${id}`, query: { edit: '1' } })
    },

    async deleteDelivery(id) {
      if (!confirm('Möchten Sie diese Lieferung wirklich löschen?')) return
      try {
        await api.delete(`delivery/${id}`)
        this.$toast?.success('Lieferung erfolgreich gelöscht')
        this.fetchDeliveries()
      } catch (error) {
        console.error('Error deleting delivery:', error)
        this.$toast?.error('Fehler beim Löschen der Lieferung')
      }
    }
  }
}
</script>

<style scoped>
.delivery-view {
  padding: 20px;
}

.page-title {
  margin-bottom: 20px;
}

.delivery-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 0.75rem;
}
.btn-csv {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.9rem;
  border: 1px solid #93c5fd;
  background: #dbeafe;
  color: #1e40af;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
}
.btn-csv:hover { background: #bfdbfe; }

.missing-inv-filter {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 12px;
  font-size: 0.9rem;
  color: #374151;
  cursor: pointer;
}

.inv-cell { cursor: pointer; }
.inv-missing { color: #9ca3af; font-size: 0.8rem; }
.inv-cell:hover .inv-missing { color: #2563eb; }
.inv-edit { display: inline-flex; align-items: center; gap: 4px; }
.inv-input { width: 90px; padding: 2px 5px; border: 1px solid #d1d5db; border-radius: 4px; }
.inv-save, .inv-cancel { border: none; background: transparent; cursor: pointer; padding: 2px 4px; }
.inv-save { color: #16a34a; }
.inv-cancel { color: #b91c1c; }
</style>
